import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../../components/shared/Header';
import Footer from '../../../components/shared/Footer';
import LoadingScreen from '../../../components/shared/LoadingScreen';
import FadeIn from '../../../components/shared/FadeIn'; // NEW
import CharacterSelector from './AppCharacter/CharacterSelector';
import CharacterModal from './AppCharacter/CharacterModal';
import CharacterImage from './AppCharacter/CharacterImage';
import StatPanel from './AppStat/StatPanel';
import AptitudePanel from './AppAptitude/AptitudePanel';
import SkillsPanel from './AppSkill/SkillsPanel';
import CostumeSelector from './AppCharacter/CostumeSelector';
import CharacterData from './AppCharacter/CharacterData';
import TotalScoreRanking from './AppScore/TotalScoreRanking';
import SkillCostDisplay from './AppScore/SkillCostDisplay';
import ScoreLimiterSkill from './AppScore/ScoreLimiterSkill';
import SupportSelector from './AppSupport/SupportSelector';
import useAppPersistence from '../../../hooks/useAppPersistence';
import useDataFetcher from '../../../hooks/useDataFetcher';
import useDataState from '../../../hooks/state/useDataState';
import useSelectionState from '../../../hooks/state/useSelectionState';
import useSkillState from '../../../hooks/state/useSkillState';
import useUIState from '../../../hooks/state/useUIState';
import useCharacterSelection from '../../../hooks/useCharacterSelection';
import useSkillManager from '../../../hooks/useSkillManager';
import useTotalScore from '../../../hooks/useTotalScore';
import useSkillOptimizer from '../../../hooks/useSkillOptimizer';
import useSaveManager from '../../../hooks/useSaveManager';
import SaveManagerModal from '../../../components/shared/SaveManagerModal';
import SaveLoadControlBar from '../../../components/shared/SaveLoadControlBar';
import ScoreCalculatorHeader from './ScoreCalculatorHeader'; // NEW
import SEO from '../../../components/shared/SEO';
// Script con nombre: useDataFetcher.js ocupa Importacion API_BASE_URL
// Script con nombre: useCharacterSelection.js ocupa Importacion API_BASE_URL


function ScoreCalculator() {
  const { t } = useTranslation();
  // Configuración inicial de la App


  // --- START: hooks/state Split ---
  // Refactor: useAppState split into 4 domain-specific hooks for better organization.

  // ... (Hooks remain the same) ...
  // 1. Data State (Global API Data)
  const {
    characters, setCharacters,
    allCards, setAllCards,
    allStats, setAllStats,
    allAptitudes, setAllAptitudes,
    allSkills, setAllSkills
  } = useDataState();

  // 2. Selection State (User Choices)
  const {
    cards, setCards,
    selectedCharacter, setSelectedCharacter,
    selectedCard, setSelectedCard,
    stats, setStats,
    editableStats, setEditableStats,
    aptitudes, setAptitudes,
    selectedStars, setSelectedStars,
    potentialLevel, setPotentialLevel
  } = useSelectionState();

  // 3. Skill State (Active Skills & Decks)
  const {
    cardSkills, setCardSkills,
    activeSkills, setActiveSkills,
    uniqueSkill, setUniqueSkill,
    uniqueSkillLevel, setUniqueSkillLevel,
    supportDeck, setSupportDeck,
    skillsRestored
  } = useSkillState();

  // 4. UI State (Loading, Modals, Limiters)
  const {
    loading, setLoading,
    isModalOpen, setIsModalOpen,
    notification, setNotification,
    isLimiterActive, setIsLimiterActive,
    limiterBudget, setLimiterBudget,
    isRestoring
  } = useUIState();

  const [isSaveManagerOpen, setIsSaveManagerOpen] = React.useState(false);
  const saveManager = useSaveManager();


  // --- START: hooks/useDataFetcher.js ---
  useDataFetcher({
    setLoading,
    setCharacters,
    setAllCards,
    setAllStats,
    setAllAptitudes,
    setAllSkills,
    setSelectedCharacter
  });
  // --- END: hooks/useDataFetcher.js ---



  // --- START: hooks/useCharacterSelection.js ---
  useCharacterSelection({
    selectedCharacter,
    allCards,
    setCards,
    isRestoring,
    setSelectedCard,
    setStats,
    allStats,
    setAptitudes,
    allAptitudes,
    selectedCard,
    setSelectedStars,
    setCardSkills,
    setActiveSkills,
    setUniqueSkill,
    stats,
    selectedStars,
    setEditableStats
  });
  // --- END: hooks/useCharacterSelection.js ---

  // --- START: hooks/useSkillManager.js ---
  const {
    handleCharacterChange,
    handleCostumeChange,
    handleStarsChange,
    handleAptitudeChange,
    handleStatChange,
    handleAddSkill,
    handleRemoveSkill,
    handleToggleSkill,
    handleLevelChange,
    handleToggleFastLearner,
    handleUpgradeSkill,
    handleDeactivateAllSkills,
    handleToggleIgnoreCost,
    handleToggleAllFastLearner,
    handleCleanSkills
  } = useSkillManager({
    selectedCard,
    cardSkills,
    selectedStars,
    setUniqueSkill,
    isRestoring,
    setUniqueSkillLevel,
    skillsRestored,
    potentialLevel,
    setActiveSkills,
    activeSkills,
    allSkills,
    setNotification,
    characters,
    setSelectedCharacter,
    cards,
    setSelectedCard,
    setSelectedStars,
    setAptitudes,
    setEditableStats
  });
  // --- END: hooks/useSkillManager.js ---

  // --- START: hooks/split Score Logic ---
  // 1. Total Score Calculation
  const { totalScore } = useTotalScore({
    selectedStars,
    uniqueSkillLevel,
    editableStats,
    activeSkills,
    aptitudes
  });

  // 2. Skill Optimizer (Limiter & Efficiency)
  const {
    currentSpent,
    insufficientPoints,
    isOverBudget,
    dynamicRankings,
    optimalSkillIds
  } = useSkillOptimizer({
    activeSkills,
    aptitudes,
    isLimiterActive,
    limiterBudget
  });
  // --- END: hooks/split Score Logic ---


  // --- START: hooks/useAppPersistence.js ---
  const { resetConfig, restoreState } = useAppPersistence({
    isRestoring,
    characters,
    setSelectedCharacter,
    allCards,
    setSelectedCard,
    setSelectedStars,
    setEditableStats,
    setAptitudes,
    setActiveSkills,
    skillsRestored,
    setUniqueSkillLevel,
    setPotentialLevel,
    setIsLimiterActive,
    setLimiterBudget,
    selectedCharacter,
    selectedCard,
    selectedStars,
    editableStats,
    aptitudes,
    activeSkills,
    uniqueSkillLevel,
    potentialLevel,
    isLimiterActive,
    limiterBudget,
    loading,
    setSupportDeck, // NEW
    supportDeck     // NEW
  });
  // --- END: hooks/useAppPersistence.js ---

  // --- START: Staggered Rendering Logic ---
  const [renderStage, setRenderStage] = React.useState(0);
  const MAX_STAGES = 9; // 0 to 9

  React.useEffect(() => {
    // Log for User debugging
    console.log(`[Staggered] App Mounted/Updated.`);

    if (loading) return; // Don't start until data is ready

    const interval = setInterval(() => {
      setRenderStage((prev) => {
        if (prev >= MAX_STAGES) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 75); // 75ms delay between stages

    return () => clearInterval(interval);
  }, [loading]); // Only run on loading change
  // --- END: Staggered Rendering Logic ---

  if (loading) {
    return <LoadingScreen />;
  }

  // Helper for Staggered Transition Delay (CSS Variable)
  const getStaggerStyle = (order) => ({
    '--stagger-delay': `${order * 50}ms` // Reduced to 50ms per step
  });

  // Construct current app state object for saving
  const currentAppState = {
    characterId: selectedCharacter?.Id_Personaje,
    cardId: selectedCard?.card_id,
    stars: selectedStars,
    stats: editableStats,
    aptitudes,
    skills: activeSkills,
    uniqueLevel: uniqueSkillLevel,
    potential: potentialLevel,
    isLimiterActive,
    limiterBudget,
    supportDeck
  };

  return (
    <>
      <SEO title={t('MetaScoreCalculator.title')} description={t('MetaScoreCalculator.description')} />
      <Header onReset={resetConfig} />
      <main className="pt-20 pb-24 min-h-screen">
        <div className="max-w-7xl mx-auto p-2 md:p-6 lg:p-8">

          <ScoreCalculatorHeader />

          {/* Cloud Save Control Bar */}
          <SaveLoadControlBar onOpenManager={() => setIsSaveManagerOpen(true)} />

          <div className="flex flex-col gap-4">
            {renderStage >= 1 && (
              <FadeIn delay={0}>
                <CharacterSelector
                  characters={characters}
                  selectedCharacter={selectedCharacter}
                  onOpenModal={() => setIsModalOpen(true)}
                  allCards={allCards}
                />
              </FadeIn>
            )}

            {renderStage >= 2 && (
              <FadeIn delay={0}>
                <CostumeSelector
                  costumes={cards}
                  selectedCostume={selectedCard}
                  onCostumeChange={handleCostumeChange}
                  selectedCharacter={selectedCharacter}
                />
              </FadeIn>
            )}

            {renderStage >= 3 && (
              <FadeIn delay={0}>
                <CharacterImage
                  character={selectedCharacter}
                  costume={selectedCard}
                  selectedStars={selectedStars}
                  onStarsChange={handleStarsChange}
                  initialStars={selectedCard?.stars_initial}
                />
              </FadeIn>
            )}

            {/* Total Score Ranking Component - Centered below image */}
            {renderStage >= 4 && (
              <FadeIn delay={0}>
                <div className="w-full max-w-[512px] mx-auto" style={getStaggerStyle(3)}>
                  <TotalScoreRanking totalScore={totalScore} />
                </div>
              </FadeIn>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-6">
            {renderStage >= 5 && (
              <FadeIn delay={0}>
                <div style={getStaggerStyle(4)}>
                  <StatPanel stats={editableStats} onStatChange={handleStatChange} />
                </div>
              </FadeIn>
            )}
            {renderStage >= 6 && (
              <FadeIn delay={0} className="relative z-30">
                {/* [STACKING FIX]: Ensure Aptitudes sit above Supports */}
                <div style={getStaggerStyle(5)}>
                  <AptitudePanel aptitudes={aptitudes} onAptitudeChange={handleAptitudeChange} />
                </div>
              </FadeIn>
            )}
          </div>

          <div className="mt-6 space-y-6">
            {renderStage >= 7 && (
              <FadeIn delay={0} className="relative z-20">
                <div style={getStaggerStyle(6)}>
                  <SupportSelector
                    slots={supportDeck}
                    setSlots={setSupportDeck}
                    activeSkills={activeSkills}
                    onAddSkill={handleAddSkill}
                    allSkills={allSkills}
                  />
                </div>
              </FadeIn>
            )}

            {/* Relocated Data & Cost Display (Side by Side) */}
            {renderStage >= 8 && (
              <FadeIn delay={0}>
                <div className="flex flex-col md:flex-row gap-6" style={getStaggerStyle(7)}>
                  <div className="flex-1">
                    <SkillCostDisplay activeSkills={activeSkills} aptitudes={aptitudes} />
                  </div>
                  <div className="flex-1">
                    <CharacterData
                      uniqueSkill={uniqueSkill}
                      uniqueSkillLevel={uniqueSkillLevel}
                      setUniqueSkillLevel={setUniqueSkillLevel}
                      potentialLevel={potentialLevel}
                      setPotentialLevel={setPotentialLevel}
                      selectedStars={selectedStars}
                    />
                  </div>
                </div>
              </FadeIn>
            )}

            {renderStage >= 9 && (
              <FadeIn delay={0}>
                <div style={getStaggerStyle(8)}>
                  <ScoreLimiterSkill
                    isLimiterActive={isLimiterActive}
                    onToggleLimiter={() => setIsLimiterActive(!isLimiterActive)}
                    budget={limiterBudget}
                    onBudgetChange={setLimiterBudget}
                    insufficientPoints={insufficientPoints}
                    currentSpent={currentSpent}
                    isOverBudget={isOverBudget}
                    onDeactivateAll={handleDeactivateAllSkills}
                    onToggleAllFastLearner={handleToggleAllFastLearner}
                    onCleanSkills={handleCleanSkills}
                  />
                  {/* Skills Panel is heavy, render last */}
                  <div className="mt-6">
                    <SkillsPanel
                      activeSkills={activeSkills}
                      onToggleSkill={handleToggleSkill}
                      onLevelChange={handleLevelChange}
                      onToggleFastLearner={handleToggleFastLearner}
                      onRemoveSkill={handleRemoveSkill}
                      allSkills={allSkills}
                      onAddSkill={handleAddSkill}
                      onUpgradeSkill={handleUpgradeSkill}
                      aptitudes={aptitudes}
                      efficiencyRankings={dynamicRankings}
                      optimalSkillIds={optimalSkillIds}
                      isLimiterActive={isLimiterActive}
                      onToggleIgnoreCost={handleToggleIgnoreCost}
                      startOrder={8} // Pass base order for staggered painting
                    />
                  </div>
                </div>
              </FadeIn>
            )}
          </div>
        </div>
      </main>
      <Footer />

      <CharacterModal
        characters={characters}
        cards={allCards}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCharacterSelect={handleCharacterChange}
      />

      <SaveManagerModal
        isOpen={isSaveManagerOpen}
        onClose={() => setIsSaveManagerOpen(false)}
        saveManager={saveManager}
        currentAppState={currentAppState}
        onLoad={restoreState}
      />

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-xl z-50 animate-fade-in-up">
          {notification}
        </div>
      )}
    </>
  );
}

export default ScoreCalculator;
// Revisado: 2026-02-21 [I18n Scan] - [Meta SEO]
